import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { API_BASE_URL } from '../utils/apiConfig';
import './EconomicCalendarPage.css';

// Country code to flag emoji mapping
const countryFlags = {
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  JPY: '🇯🇵',
  AUD: '🇦🇺',
  CAD: '🇨🇦',
  CHF: '🇨🇭',
  NZD: '🇳🇿',
  CNY: '🇨🇳',
  HKD: '🇭🇰',
  SGD: '🇸🇬',
  INR: '🇮🇳',
  KRW: '🇰🇷',
  MXN: '🇲🇽',
  BRL: '🇧🇷',
  ZAR: '🇿🇦',
  TRY: '🇹🇷',
  RUB: '🇷🇺',
  SEK: '🇸🇪',
  NOK: '🇳🇴',
  DKK: '🇩🇰',
  PLN: '🇵🇱',
  CZK: '🇨🇿',
  HUF: '🇭🇺',
  ILS: '🇮🇱',
  ARS: '🇦🇷',
  CLP: '🇨🇱',
  COP: '🇨🇴',
  IDR: '🇮🇩',
  MYR: '🇲🇾',
  PHP: '🇵🇭',
  THB: '🇹🇭',
  VND: '🇻🇳',
  ALL: '🌍'
};

const countryNames = {
  USD: 'United States',
  EUR: 'European Union',
  GBP: 'United Kingdom',
  JPY: 'Japan',
  AUD: 'Australia',
  CAD: 'Canada',
  CHF: 'Switzerland',
  NZD: 'New Zealand',
  CNY: 'China',
  HKD: 'Hong Kong',
  SGD: 'Singapore',
  INR: 'India',
  KRW: 'South Korea',
  MXN: 'Mexico',
  BRL: 'Brazil',
  ZAR: 'South Africa',
  TRY: 'Turkey',
  RUB: 'Russia',
  SEK: 'Sweden',
  NOK: 'Norway',
  DKK: 'Denmark',
  PLN: 'Poland',
  CZK: 'Czech Republic',
  HUF: 'Hungary',
  ILS: 'Israel',
  ALL: 'All Countries'
};

function EconomicCalendarPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('today');
  const [importanceFilter, setImportanceFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/economic-calendar?limit=500`);
      const data = await response.json();

      if (data.success) {
        // Map parser data to page format
        const mappedEvents = (data.events || []).map(event => ({
          ...event,
          date: event.datetime, // Parser uses 'datetime', page expects 'date'
          country: event.currency, // Parser uses 'currency' as country code
        }));
        setEvents(mappedEvents);
      } else {
        setError(data.error || 'Failed to fetch events');
      }
    } catch (err) {
      setError('Failed to fetch economic events');
      console.error('Economic events fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    // Refresh every 5 minutes
    const interval = setInterval(fetchEvents, 300000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  // Parse date from event
  const parseEventDate = (dateStr) => {
    return new Date(dateStr);
  };

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filter) {
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: today };
      }
      case 'today': {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return { start: today, end: tomorrow };
      }
      case 'tomorrow': {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayAfter = new Date(tomorrow);
        dayAfter.setDate(dayAfter.getDate() + 1);
        return { start: tomorrow, end: dayAfter };
      }
      case 'week': {
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return { start: today, end: weekEnd };
      }
      case 'twoWeeks': {
        const twoWeeksEnd = new Date(today);
        twoWeeksEnd.setDate(twoWeeksEnd.getDate() + 14);
        return { start: today, end: twoWeeksEnd };
      }
      default:
        return { start: today, end: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000) };
    }
  };

  // Filter and group events
  const filteredEvents = useMemo(() => {
    const { start, end } = getDateRange(dateFilter);

    return events.filter(event => {
      const eventDate = parseEventDate(event.date);

      // Date filter
      if (eventDate < start || eventDate >= end) return false;

      // Importance filter
      if (importanceFilter !== 'all') {
        const impact = event.impact?.toLowerCase();
        if (importanceFilter === 'high' && impact !== 'high') return false;
        if (importanceFilter === 'medium' && impact !== 'medium' && impact !== 'high') return false;
      }

      // Country filter
      if (countryFilter !== 'all' && event.country !== countryFilter) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const title = (event.title || '').toLowerCase();
        const country = (countryNames[event.country] || '').toLowerCase();
        if (!title.includes(query) && !country.includes(query)) return false;
      }

      return true;
    });
  }, [events, dateFilter, importanceFilter, countryFilter, searchQuery]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups = {};
    filteredEvents.forEach(event => {
      const date = parseEventDate(event.date);
      const dateKey = date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });

    // Sort events within each group by time
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(a.date) - new Date(b.date));
    });

    return groups;
  }, [filteredEvents]);

  // Count events by importance for date buttons
  const getEventCounts = (filter) => {
    const { start, end } = getDateRange(filter);
    let high = 0, medium = 0, low = 0;

    events.forEach(event => {
      const eventDate = parseEventDate(event.date);
      if (eventDate >= start && eventDate < end) {
        const impact = event.impact?.toLowerCase();
        if (impact === 'high') high++;
        else if (impact === 'medium') medium++;
        else low++;
      }
    });

    return { high, medium, low };
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    const formatted = date.toLocaleDateString('en-US', options);

    if (date.toDateString() === today.toDateString()) {
      return `${formatted} (Today)`;
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return `${formatted} (Tomorrow)`;
    }
    return formatted;
  };

  const getImportanceClass = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high': return 'importance-high';
      case 'medium': return 'importance-medium';
      default: return 'importance-low';
    }
  };

  const uniqueCountries = useMemo(() => {
    const countries = new Set(events.map(e => e.country));
    return Array.from(countries).sort();
  }, [events]);

  return (
    <div className="economic-calendar-page">
      <SEO page="economicCalendar" />
      <Header />

      <div className="economic-calendar-content">
        <div className="economic-calendar-header">
          <h1 className="economic-calendar-title">{t('economicCalendar.title', 'Economic Calendar')}</h1>
          <p className="economic-calendar-subtitle">
            {t('economicCalendar.subtitle', 'Track global macroeconomic events and economic indicators')}
          </p>
        </div>

        {/* Date Filters */}
        <div className="date-filters">
          {['yesterday', 'today', 'tomorrow', 'week', 'twoWeeks'].map(filter => {
            const counts = getEventCounts(filter);
            const labels = {
              yesterday: t('economicCalendar.yesterday', 'Yesterday'),
              today: t('economicCalendar.today', 'Today'),
              tomorrow: t('economicCalendar.tomorrow', 'Tomorrow'),
              week: t('economicCalendar.nextWeek', 'Next 7 days'),
              twoWeeks: t('economicCalendar.nextTwoWeeks', 'Next 14 days')
            };
            return (
              <button
                key={filter}
                className={`date-button ${dateFilter === filter ? 'active' : ''}`}
                onClick={() => setDateFilter(filter)}
              >
                <span className="date-label">{labels[filter]}</span>
                <div className="date-badges">
                  {counts.high > 0 && <span className="badge badge-high">{counts.high}</span>}
                  {counts.medium > 0 && <span className="badge badge-medium">{counts.medium}</span>}
                  {counts.low > 0 && <span className="badge badge-low">{counts.low}</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Filters Row */}
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('economicCalendar.importance', 'Importance')}</label>
            <select
              value={importanceFilter}
              onChange={(e) => setImportanceFilter(e.target.value)}
            >
              <option value="all">{t('economicCalendar.allLevels', 'All Levels')}</option>
              <option value="high">{t('economicCalendar.highOnly', 'High Only')}</option>
              <option value="medium">{t('economicCalendar.mediumHigh', 'Medium & High')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('economicCalendar.country', 'Country/Region')}</label>
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <option value="all">{t('economicCalendar.allCountries', 'All Countries')}</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>
                  {countryFlags[country] || ''} {countryNames[country] || country}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group search-group">
            <label>{t('economicCalendar.search', 'Search')}</label>
            <div className="search-wrapper">
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder={t('economicCalendar.searchPlaceholder', 'Search for events')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Events */}
        {loading ? (
          <div className="economic-calendar-loading">
            <div className="spinner"></div>
            <p>{t('economicCalendar.loading', 'Loading events...')}</p>
          </div>
        ) : error ? (
          <div className="economic-calendar-error">
            <p>{error}</p>
            <button onClick={fetchEvents}>{t('economicCalendar.retry', 'Retry')}</button>
          </div>
        ) : Object.keys(groupedEvents).length === 0 ? (
          <div className="economic-calendar-empty">
            <p>{t('economicCalendar.noEvents', 'No events found for the selected filters')}</p>
          </div>
        ) : (
          Object.entries(groupedEvents)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .map(([dateKey, dayEvents]) => (
              <div key={dateKey} className="events-day-group">
                <h2 className="date-header">{formatDateHeader(dateKey)}</h2>
                <div className="events-table-container">
                  <table className="events-table">
                    <thead>
                      <tr>
                        <th>{t('economicCalendar.time', 'TIME')}</th>
                        <th>{t('economicCalendar.countryColumn', 'COUNTRY')}</th>
                        <th>{t('economicCalendar.importanceColumn', 'IMPORTANCE')}</th>
                        <th>{t('economicCalendar.event', 'EVENT')}</th>
                        <th>{t('economicCalendar.forecast', 'FORECAST')}</th>
                        <th>{t('economicCalendar.previous', 'PREVIOUS')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayEvents.map((event, index) => (
                        <tr key={`${event.date}-${index}`}>
                          <td className="time-cell">{formatTime(event.date)}</td>
                          <td className="country-cell">
                            <span className="country-flag">{countryFlags[event.country] || '🌍'}</span>
                            <span className="country-name">{countryNames[event.country] || event.country}</span>
                          </td>
                          <td>
                            <span className={`importance-badge ${getImportanceClass(event.impact)}`}>
                              {event.impact || 'Low'}
                            </span>
                          </td>
                          <td className="event-name">{event.title}</td>
                          <td className="data-cell">{event.forecast || '-'}</td>
                          <td className="data-cell">{event.previous || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
}

export default EconomicCalendarPage;
